import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const TYPE_COLORS = {
  commentary: { bar: 'bg-blue-400', dot: 'bg-blue-400', label: 'Commentary', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  risk_mention: { bar: 'bg-orange-400', dot: 'bg-orange-400', label: 'Risk', badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  conditional_prediction: { bar: 'bg-yellow-400', dot: 'bg-yellow-400', label: 'Conditional', badge: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
};

function TypeBar({ commentaryCount, riskCount, conditionalCount }) {
  const total = commentaryCount + riskCount + conditionalCount;
  if (total === 0) return null;
  const commPct = Math.round((commentaryCount / total) * 100);
  const riskPct = Math.round((riskCount / total) * 100);
  const condPct = 100 - commPct - riskPct;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex rounded-full overflow-hidden h-1.5 w-full">
        {commPct > 0 && <div className="bg-blue-400" style={{ width: `${commPct}%` }} />}
        {condPct > 0 && <div className="bg-yellow-400" style={{ width: `${condPct}%` }} />}
        {riskPct > 0 && <div className="bg-orange-400" style={{ width: `${riskPct}%` }} />}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-gray-400">
        {commentaryCount > 0 && (
          <span className="flex items-center gap-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />
            {commentaryCount} Commentary
          </span>
        )}
        {riskCount > 0 && (
          <span className="flex items-center gap-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
            {riskCount} Risk
          </span>
        )}
        {conditionalCount > 0 && (
          <span className="flex items-center gap-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 inline-block" />
            {conditionalCount} Conditional
          </span>
        )}
      </div>
    </div>
  );
}

function relativeTime(isoString) {
  if (!isoString) return null;
  const diff = Date.now() - new Date(isoString).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default function CommentarySymbolRow({ item }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div
        className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Symbol */}
        <div className="w-32 flex-shrink-0">
          <div className="font-bold text-gray-900 text-sm">{item.ticker}</div>
          <div className="text-xs text-gray-400 truncate">{item.name}</div>
        </div>

        {/* Type bar */}
        <div className="flex-1 min-w-0">
          <TypeBar
            commentaryCount={item.commentary_count}
            riskCount={item.risk_count}
            conditionalCount={item.conditional_count}
          />
        </div>

        {/* Right side: count + recency */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-gray-500 font-medium">
            {item.mention_count} mention{item.mention_count !== 1 ? 's' : ''}
          </span>
          {item.last_mentioned && (
            <span className="text-[10px] text-gray-400 hidden sm:block">
              {relativeTime(item.last_mentioned)}
            </span>
          )}
        </div>

        <div className="text-gray-300 flex-shrink-0">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-50 px-4 pb-4 pt-3 flex flex-col gap-3">
          {item.key_quotes && item.key_quotes.length > 0 ? (
            item.key_quotes.map((q, i) => {
              const style = TYPE_COLORS[q.mention_type] ?? TYPE_COLORS.commentary;
              return (
                <div key={i} className="bg-gray-50 rounded-lg px-3 py-2.5">
                  <p className="text-xs text-gray-700 italic line-clamp-3 mb-2">"{q.quote}"</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${style.badge}`}>
                      {style.label}
                    </span>
                    {q.published_at && (
                      <span className="text-[10px] text-gray-400">
                        {new Date(q.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-xs text-gray-400 text-center py-2">No quotes available for this symbol.</p>
          )}
        </div>
      )}
    </div>
  );
}
